<script setup lang="ts">
  import { ref, computed, onMounted } from "vue";
  import { useI18n } from "vue-i18n";
  import { Chip, Text, IconArrowLeft, IconFilter } from "@ui-kit";
  import { useProfileOptions } from "@src/hooks";
  import { StatusBar, TopBar, OrderItem } from "@src/components";
  import { filterOrders } from "@src/utils";
  import {
    useOrderListStore,
    useExecutorListStore,
    useSettingsStore,
    useOrderListFiltersStore,
    useWidgetsStore,
    type Filter,
  } from "@src/store";
  import { sortOrders } from "./utils";
  import { default as OrdersNotFound } from "./OrdersNotFound.vue";
  import { default as OrdersFilterModal } from "./OrdersFilterModal.vue";

  const emit = defineEmits<{ (e: "close"): void }>();

  const isFilterModalVisible = ref(false);

  const { t } = useI18n();
  const { profile } = useProfileOptions();
  const executorListStore = useExecutorListStore();
  const orderListStore = useOrderListStore();
  const order_list_filters_store = useOrderListFiltersStore();
  const settingsStore = useSettingsStore();
  const widgets_store = useWidgetsStore();

  const filterLabel = {
    new: t("Новые"),
    my: t("Мои"),
    in_progress: t("В работе"),
    done: t("Готовые"),
  };

  const handleFilterChipClick = (chip: Filter) => {
    order_list_filters_store.update_status(chip.key, !chip.value);
  };

  const handleOrdersClose = () => {
    widgets_store.close("order_card");

    emit("close");
  };

  const filteredOrderList = computed(() => {
    const orders = orderListStore.ids.map((orderId) => orderListStore.map[orderId]);
    const historyOrders = orderListStore.historyIds.map((orderId) => orderListStore.map[orderId]);

    const filtered = filterOrders(
      [...orders, ...historyOrders],
      order_list_filters_store.active_status_key,
      profile.value.current_user.user_id,
      order_list_filters_store.section.value,
      order_list_filters_store.store.value,
    );

    return sortOrders(filtered);
  });

  onMounted(() => {
    if (settingsStore.location === "store") {
      order_list_filters_store.update_status("new", true);
    }
  });
</script>

<template>
  <div class="orders-widget" data-testid="orders-widget">
    <StatusBar />
    <TopBar :title="$t('Запросы')" @click:left="handleOrdersClose" data-testid="orders-top-bar">
      <template #left>
        <IconArrowLeft />
      </template>
    </TopBar>
    <div class="filter-chips">
      <Chip
        v-for="chip in order_list_filters_store.status_list"
        size="s"
        :key="chip.key"
        :label="filterLabel[chip.key]"
        :selected="chip.active"
        :data-testid="`filter-chip-${chip.key}`"
        @click="handleFilterChipClick(chip)"
      />
    </div>
    <OrdersNotFound v-if="filteredOrderList.length === 0" />
    <div v-else class="order-list" data-testid="order-list">
      <OrderItem
        v-for="order in filteredOrderList"
        :key="order.order_id"
        :order_status="order.status"
        :order_type="order.order_type"
        :order_total_items="order.goods.reduce((acc, good) => acc + good.quantity, 0)"
        :order_place_from="order.place_from"
        :store="order.place_from_type"
        :order_place_section="order.place_section"
        :order_place_to="order.place_to"
        :order_number="order.order_number?.toUpperCase() || ''"
        :createdAt="order.created"
        :executerDisplayName="executorListStore.map[order.executed_by]?.display_name"
        :partial="order.partial"
        :comment="order.comment"
        @click="
          () => {
            widgets_store.open('order_card', { order_id: order.order_id, back: 'orders', index: 2 });
          }
        "
      />
    </div>
    <div class="orders-widget__filter-btn" @click="isFilterModalVisible = true" data-testid="filter-btn">
      <div v-if="order_list_filters_store.count > 0" class="orders-widget__filter-counter" data-testid="filter-counter">
        <Text font="body-medium-12" color="white">{{ order_list_filters_store.count }}</Text>
      </div>
      <IconFilter style="width: 32px; height: 32px" />
    </div>
    <OrdersFilterModal v-if="isFilterModalVisible" @close="isFilterModalVisible = false" />
  </div>
</template>

<style scoped>
  .orders-widget {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    background-color: var(--sf-base-background-white);
    z-index: 1;

    .filter-chips {
      display: flex;
      flex-shrink: 0;
      gap: 4px;
      overflow-x: auto;
      padding: 0 16px 16px;

      & > div {
        flex: 1;
        justify-content: center;
      }
    }

    .order-list {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 8px;
      padding: 16px 8px;
      overflow-y: auto;
      background-color: var(--sf-base-background-primary);

      & :deep(.order-item) {
        max-width: 344px;
      }
    }
  }
  .orders-widget__filter-btn {
    position: absolute;
    bottom: 16px;
    right: 8px;
    width: 64px;
    height: 64px;
    background-color: var(--sf-base-background-tabbar);
    border-radius: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--sf-shadow-glass);
  }

  .orders-widget__filter-counter {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
    background-color: var(--sf-base-icon-error);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
